import { useState } from 'react';

import { fetchWithLimit, useDashboardResource } from './resource';

import {
  EducationPriorityApi,
  EducationProgressApi,
  ExpertiseApi,
  KnowledgeAreaApi,
  PositionAPI,
  axiosApiInstance,
} from 'services';
import { IAccountUserResponse } from 'services/account/account.interface';
import { IEducationPriority } from 'services/education-priority';
import { IExerciseStatusRecord } from 'services/education-progress/education-progress.interface';
import { IPositionResponseNew } from 'services/position/position.interface';
import { IExpertise } from 'types/domain/expertise';
import { IKnowledgeArea } from 'types/domain/knowledgeArea';
import { getStorageValue, saveState } from 'utils/localStorageFilters';

export { invalidateDashboardData } from './resource';

// ── Date range ────────────────────────────────────────────────────────────────
// One shared, persisted period selection: picking a range on the dashboard
// applies to the drilldown pages too (and vice versa).

export const DATE_RANGE_OPTIONS = ['Last 30 days', 'Last 6 months', 'Last year'] as const;
export type DateRangeOption = (typeof DATE_RANGE_OPTIONS)[number];

const HALF_YEAR_MONTHS = 6;
const HALF_YEAR_DAYS = 183;
const YEAR_DAYS = 365;
const MONTH_DAYS = 30;
const PROGRESS_REQUEST_CONCURRENCY = 8;

const DATE_RANGE_KEY = 'dashboard_date_range';
const DEFAULT_DATE_RANGE: DateRangeOption = 'Last 30 days';

export function dateRangeFrom(range: DateRangeOption): Date {
  const now = new Date();
  switch (range) {
    case 'Last 6 months':
      return new Date(
        now.getFullYear(),
        now.getMonth() - HALF_YEAR_MONTHS,
        now.getDate(),
      );
    case 'Last year':
      return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    case 'Last 30 days':
    default: {
      const d = new Date();
      d.setDate(d.getDate() - MONTH_DAYS);
      return d;
    }
  }
}

export function dateRangeDays(range: DateRangeOption): number {
  switch (range) {
    case 'Last 6 months':
      return HALF_YEAR_DAYS;
    case 'Last year':
      return YEAR_DAYS;
    case 'Last 30 days':
    default:
      return MONTH_DAYS;
  }
}

export function useDashboardDateRange(): [
  DateRangeOption,
  (range: DateRangeOption) => void,
] {
  const [range, setRange] = useState<DateRangeOption>(() => {
    const stored = getStorageValue(DATE_RANGE_KEY, DEFAULT_DATE_RANGE);
    return DATE_RANGE_OPTIONS.includes(stored) ? stored : DEFAULT_DATE_RANGE;
  });

  const update = (next: DateRangeOption): void => {
    setRange(next);
    saveState(DATE_RANGE_KEY, next);
  };

  return [range, update];
}

export function useOrgAccounts(orgId: number | undefined): {
  accounts: IAccountUserResponse[];
  loading: boolean;
  error: string | null;
} {
  const { data: accounts, ...status } = useDashboardResource<IAccountUserResponse[]>(
    orgId ? `accounts:${orgId}` : null,
    [],
    async () =>
      (
        await axiosApiInstance.get<IAccountUserResponse[]>(
          `account/organization/${orgId}`,
        )
      ).data,
  );
  return { accounts, ...status };
}
export function useOrgExpertises(orgId: number | undefined): {
  expertises: IExpertise[];
  loading: boolean;
  error: string | null;
} {
  const { data: expertises, ...status } = useDashboardResource<IExpertise[]>(
    orgId ? `expertises:${orgId}` : null,
    [],
    () => new ExpertiseApi().getAllExpertisesByOrgId(orgId!),
  );
  return { expertises, ...status };
}
export function useOrgKnowledgeAreas(orgId: number | undefined): {
  knowledgeAreas: IKnowledgeArea[];
  loading: boolean;
  error: string | null;
} {
  const { data: knowledgeAreas, ...status } = useDashboardResource<IKnowledgeArea[]>(
    orgId ? `knowledge-areas:${orgId}` : null,
    [],
    () => new KnowledgeAreaApi().getAllByOrgId(orgId!),
  );
  return { knowledgeAreas, ...status };
}
export function useOrgEducationPriorities(orgId: number | undefined): {
  priorities: IEducationPriority[];
  loading: boolean;
  error: string | null;
} {
  const { data: priorities, ...status } = useDashboardResource<IEducationPriority[]>(
    orgId ? `priorities:${orgId}` : null,
    [],
    () => new EducationPriorityApi().getAll(),
  );
  return { priorities, ...status };
}
export function useAllPositions(expertiseIds: number[]): {
  positions: IPositionResponseNew[];
  positionExpertiseMap: Map<number, number>;
  loading: boolean;
  error: string | null;
} {
  const { data, ...status } = useDashboardResource(
    expertiseIds.length ? `positions:${expertiseIds.join(',')}` : null,
    {
      positions: [] as IPositionResponseNew[],
      positionExpertiseMap: new Map<number, number>(),
    },
    async () => {
      const arrays = await Promise.all(
        expertiseIds.map(id => new PositionAPI().getAllPositionsByExpertise(id)),
      );
      const positionExpertiseMap = new Map<number, number>();
      const positions = new Map<number, IPositionResponseNew>();
      arrays.forEach((items, index) =>
        items.forEach(position => {
          positionExpertiseMap.set(position.id, expertiseIds[index]);
          positions.set(position.id, position);
        }),
      );
      return { positions: Array.from(positions.values()), positionExpertiseMap };
    },
  );
  return { ...data, ...status };
}
export function useAllProgress(accountIds: number[]): {
  allProgress: Map<number, IExerciseStatusRecord[]>;
  loading: boolean;
  error: string | null;
} {
  const { data: allProgress, ...status } = useDashboardResource(
    accountIds.length ? `progress:${accountIds.join(',')}` : null,
    new Map<number, IExerciseStatusRecord[]>(),
    async () => {
      const api = new EducationProgressApi();
      const entries = await fetchWithLimit(
        accountIds.map(
          id => async () =>
            [id, await api.getEducationProgressByAccount(id)] as [
              number,
              IExerciseStatusRecord[],
            ],
        ),
        PROGRESS_REQUEST_CONCURRENCY,
      );
      return new Map(entries);
    },
  );
  return { allProgress, ...status };
}
